import { ReactNode, useEffect, useState } from "react";
import { useTelegram } from "@/contexts/TelegramContext";
import { useTranslation } from "react-i18next";
import { AlertCircle, ExternalLink } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface TelegramPlatformGuardProps {
  children: ReactNode;
}

/**
 * TelegramPlatformGuard - компонент защиты от запуска вне Telegram
 *
 * Проверяет наличие Telegram WebApp данных и блокирует доступ к приложению,
 * если оно открыто не из Telegram Mini Apps.
 *
 * Использование:
 * <TelegramPlatformGuard>
 *   <App />
 * </TelegramPlatformGuard>
 */
export const TelegramPlatformGuard: React.FC<TelegramPlatformGuardProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { webApp, isReady, initData } = useTelegram();
  const [isValidPlatform, setIsValidPlatform] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    // Проверяем наличие Telegram WebApp и initData
    const hasWebApp = !!webApp;
    const hasInitData = !!initData && initData.length > 0;

    interface TelegramWindow {
      Telegram?: {
        WebApp?: Record<string, unknown>;
      };
    }

    const win =
      typeof window !== "undefined"
        ? (window as unknown as TelegramWindow)
        : null;
    const isPlatform =
      win !== null &&
      typeof win.Telegram !== "undefined" &&
      typeof win.Telegram?.WebApp !== "undefined";

    const isValid = hasWebApp && hasInitData && isPlatform;

    console.log("[TelegramPlatformGuard] Platform validation:", {
      hasWebApp,
      hasInitData,
      isPlatform,
      isValid,
      initDataLength: initData?.length || 0,
    });

    setIsValidPlatform(isValid);
  }, [webApp, isReady, initData]);

  // Показываем загрузку пока проверяем платформу
  if (isValidPlatform === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">
            {t("common.initializing")}
          </p>
        </div>
      </div>
    );
  }

  // Если платформа невалидна, показываем ошибку
  if (!isValidPlatform) {
    const botUsername =
      import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "trafomatic_bot";
    const telegramUrl = `https://t.me/${botUsername}`;

    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold">
              {t("telegramOnly.title")}
            </CardTitle>
            <CardDescription className="text-base">
              {t("telegramOnly.description")}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-sm">
                {t("telegramOnly.howToAccessTitle")}
              </h3>
              <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                <li>{t("telegramOnly.step1")}</li>
                <li>{t("telegramOnly.step2")}</li>
                <li>{t("telegramOnly.step3")}</li>
              </ol>
            </div>

            <Button
              className="w-full gap-2"
              onClick={() => window.open(telegramUrl, "_blank")}
            >
              <ExternalLink className="w-4 h-4" />
              {t("telegramOnly.openInTelegram")}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              {t("telegramOnly.needHelp")}{" "}
              <a
                href={`${telegramUrl}?start=help`}
                className="text-primary hover:underline"
              >
                {t("telegramOnly.contactSupport")}
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Платформа валидна, показываем приложение
  return <>{children}</>;
};
